import { keepPreviousQueryData, useInfiniteQuery } from '@northladder/services';

import { BIDS_API } from '../bidsAPIClient';
import { IFetchStagedBidsPayload } from '../types';

/**
 * Keeps track of the staged bids query key.
 */
const STAGED_BIDS_QUERY_KEY = 'staged-bids-query-key';

/**
 * -----------------------------------------------------------------------------
 * React Query hook for fetching the staged bids using `fetchStagedBids` api
 * give error loading success state with response
 */
export const useStagedBidsQuery = (queryParams: IFetchStagedBidsPayload) => {
  const {
    bidValidityConfigId,
    brandIds,
    categoryId,
    count,
    groupIds,
    page,
    search,
    winningStatus,
  } = queryParams;

  const { data, isError, error, isFetching, isLoading, isPending, refetch } =
    useInfiniteQuery({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        STAGED_BIDS_QUERY_KEY,
        bidValidityConfigId,
        categoryId,
        page,
        count,
        brandIds,
        groupIds,
        search,
        winningStatus,
      ],
      networkMode: 'always', // Even make calls when offline
      queryFn: () => BIDS_API.fetchStagedBids(queryParams),
      initialPageParam: 1,
      placeholderData: keepPreviousQueryData,
      // staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        const currentCount = allPages.length * count;

        return currentCount < lastPage.totalCount ? nextPage : undefined;
      },
    });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    isPending,
    refetch,
  };
};
